/**
 * MediaSwitch
 *
 * Selects the correct media variant (portrait or landscape) at runtime based on
 * the current orientation. This prevents downloading both assets upfront.
 *
 * HOW IT WORKS:
 * ──────────────────────────────────────────────────────────────────────────────
 * - In HTML you define <template> nodes for each variant:
 *      <template data-media-switch-template="portrait"> … </template>
 *      <template data-media-switch-template="landscape"> … </template>
 * - MediaSwitch injects the chosen template’s content into the slot element:
 *      [data-media-switch-slot]
 * - Only the chosen variant receives a real <img>/<video> tag in DOM → no double
 *   downloads → better performance.
 * - On orientation change the component re-renders and can re-initialise child
 *   components (via `onSwitch` callback or the dispatched event).
 *
 * IMPORTANT:
 * ──────────────────────────────────────────────────────────────────────────────
 * - This is ideal for **generic / flexible media blocks** throughout a site,
 *   especially where media type can differ (e.g. video vs image).
 *
 * - NOT recommended for LCP hero media:
 *   For the absolute hero section (LCP target) prefer pure HTML <picture>/<source>
 *   if both variants are the same type. That allows the browser to pick the asset
 *   immediately during parsing (no JS delay).
 *
 * Events:
 *  - Emits `media-switch:rendered` after each insert → other systems can hook in.
 *
 * Minimal HTML structure example:
 * ──────────────────────────────────────────────────────────────────────────────
 * <div data-media-switch>
 *   <div data-media-switch-slot style="display:contents;"></div>
 *   <template data-media-switch-template="portrait"> … </template>
 *   <template data-media-switch-template="landscape"> … </template>
 * </div>
 */

export class MediaSwitch {
	constructor(el, options) {
		this.options = {
			onSwitch: () => {},
			...options,
		}
		const root = el
		this.root = root
		this.slot = root.querySelector('[data-media-switch-slot]')
		this.tpls = root.querySelectorAll('[data-media-switch-template]')
		this.mq = matchMedia('(orientation:portrait)')

		this.update = this.update.bind(this)

		this.init()
	}

	init() {
		// if only 1 template — no switching needed
		if (this.tpls.length === 1) {
			this.render(this.tpls[0])
			return
		}

		// multi variant → do initial + listen to changes
		this.update()
		this.mq.addEventListener('change', this.update)
	}

	render(tpl) {
		this.slot.innerHTML = ''
		this.slot.append(tpl.content.cloneNode(true))
		this.options.onSwitch(this.root)

		this.root.dispatchEvent(
			new CustomEvent('media-switch:rendered', {
				bubbles: true,
			})
		)
	}

	update() {
		const tpl = this.root.querySelector(
			`[data-media-switch-template="${
				this.mq.matches ? 'portrait' : 'landscape'
			}"]`
		)
		this.render(tpl)
	}
	destroy() {
		this.mq.removeEventListener('change', this.update)
	}
}
